import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { getDashboardV3PerpetualDetail } from '../services/dashboardV3.service';
import type {
    DashboardV3PerpetualDetailParams,
    DashboardV3PerpetualDetailRow,
} from '../services/dashboardV3.types';

interface UseDashboardV3PerpetualDetailOptions {
    // 仅在合约 tab 启用，其他产品 tab 不发起请求
    enabled?: boolean;
}

/**
 * 合约 tab 下的 Trading Detail：永续逐笔成交明细（状态固定 FILLED）。
 */
export function useDashboardV3PerpetualDetail(
    params: DashboardV3PerpetualDetailParams,
    options: UseDashboardV3PerpetualDetailOptions = {}
) {
    const { enabled = true } = options;
    const notify = useNotify();
    const t = useTranslate();

    const [list, setList] = useState<DashboardV3PerpetualDetailRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const queryParams = useMemo(
        () => ({
            page: params.page,
            size: params.size,
            userId: params.userId,
            walletAddress: params.walletAddress,
            startDate: params.startDate,
            endDate: params.endDate,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        }),
        [
            params.page,
            params.size,
            params.userId,
            params.walletAddress,
            params.startDate,
            params.endDate,
            params.sortBy,
            params.sortOrder,
        ]
    );

    useEffect(() => {
        if (!enabled) {
            setList([]);
            setTotal(0);
            return;
        }

        let alive = true;

        setLoading(true);

        getDashboardV3PerpetualDetail(queryParams)
            .then((resp) => {
                if (!alive) return;
                const rows =
                    resp?.items ?? resp?.data ?? resp?.list ?? resp?.records ?? [];
                setList(
                    rows.map((r, i) =>
                        r?.id != null ? r : { ...r, id: `${queryParams.page}-${i}` }
                    )
                );
                setTotal(Number(resp?.total ?? rows.length));
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(
                    e?.message ||
                    t('module.dashboardV3.trading.messages.loadFailed', {
                        _: 'Failed to load trading data',
                    }),
                    { type: 'warning' }
                );
                setList([]);
                setTotal(0);
            })
            .finally(() => {
                if (!alive) return;
                setLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [enabled, queryParams, notify, t]);

    return { list, total, loading };
}
